import { useState } from 'react'
import type { FieldValues, UseFormReturn } from 'react-hook-form'

interface UseCloseGuardOptions {
	isLoading?: boolean
	/** Called after form.reset() on confirm — use to clear persisted drafts etc. */
	onReset?: () => void
}

/**
 * Guards a Sheet/Dialog from being closed when the form has unsaved changes.
 *
 * - Blocks close entirely while `isLoading` (mutation in flight).
 * - Shows a confirmation prompt when the form is dirty but not submitting.
 * - Calls `form.reset()` + optional `onReset` when the user confirms discard.
 */
export function useCloseGuard<T extends FieldValues>(
	form: UseFormReturn<T>,
	onClose: (open: boolean) => void,
	options?: UseCloseGuardOptions,
) {
	const { isLoading, onReset } = options ?? {}
	const [confirmOpen, setConfirmOpen] = useState(false)

	function handleOpenChange(open: boolean) {
		if (!open && isLoading) return
		if (!open && form.formState.isDirty) {
			setConfirmOpen(true)
			return
		}
		onClose(open)
	}

	function handleConfirmDiscard() {
		form.reset()
		onReset?.()
		setConfirmOpen(false)
		onClose(false)
	}

	function handleCancelDiscard() {
		setConfirmOpen(false)
	}

	return { handleOpenChange, confirmOpen, handleConfirmDiscard, handleCancelDiscard }
}
